import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
export function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    let particles: Particle[] = [];
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 1.5 + 0.5;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.fill();
      }
    }
    const initParticles = () => {
      particles = [];
      const numParticles = Math.floor(window.innerWidth * window.innerHeight / 12000);
      for (let i = 0; i < numParticles; i++) {
        particles.push(new Particle());
      }
    };
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            const opacity = 1 - distance / 150;
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity * 0.15})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    window.addEventListener('resize', resize);
    resize();
    animate();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 px-6 bg-hero-gradient overflow-hidden min-h-[70vh] flex items-center" data-id="element-309" data-mp="112g7vndqwf9a">
      {/* Particle Network Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" data-id="element-310" data-mp="x3h2k1o3qghb" />

      {/* Ambient Glow Orbs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-311" data-mp="3hr3s5wvh8d6c" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-312" data-mp="1xb5qyhfqoxhp" />

      {/* ═══ CLEAN WIREFRAME BRAIN ═══ */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-313" data-mp="2697o2lwcr5zp">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 2
      }} style={{
        width: '1100px',
        height: '1100px'
      }} data-id="element-314" data-mp="2b1hkmkrz34ay">
          <svg viewBox="0 0 500 500" className="w-full h-full" data-id="element-315" data-mp="2p1sc9q13wmx6">
            {/* ── Constellation Lines — uniform thin crisp strokes ── */}
            {[[155, 120, 200, 175], [200, 175, 175, 245], [175, 245, 220, 310], [220, 310, 195, 375], [200, 175, 250, 210], [250, 210, 250, 280], [250, 280, 250, 350], [345, 120, 300, 175], [300, 175, 325, 245], [325, 245, 280, 310], [280, 310, 305, 375], [300, 175, 250, 210], [175, 245, 250, 280], [325, 245, 250, 280], [220, 310, 250, 350], [280, 310, 250, 350], [130, 195, 200, 175], [370, 195, 300, 175], [130, 285, 175, 245], [370, 285, 325, 245], [155, 350, 220, 310], [345, 350, 280, 310], [250, 140, 200, 175], [250, 140, 300, 175], [250, 140, 250, 210], [195, 375, 250, 400], [305, 375, 250, 400], [250, 350, 250, 400]].map(([x1, y1, x2, y2], i) => <line key={`l-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" data-id="element-316" data-mp="2yy8244vs2d3x" data-mpi={i} />)}

            {/* ── Star Nodes — sharp uniform pinpoints ── */}
            {[{
            x: 250,
            y: 140,
            r: 1.5
          }, {
            x: 155,
            y: 120,
            r: 1.2
          }, {
            x: 345,
            y: 120,
            r: 1.2
          }, {
            x: 200,
            y: 175,
            r: 1.5
          }, {
            x: 300,
            y: 175,
            r: 1.5
          }, {
            x: 130,
            y: 195,
            r: 1
          }, {
            x: 370,
            y: 195,
            r: 1
          }, {
            x: 250,
            y: 210,
            r: 1.8
          }, {
            x: 175,
            y: 245,
            r: 1.5
          }, {
            x: 325,
            y: 245,
            r: 1.5
          }, {
            x: 250,
            y: 280,
            r: 1.8
          }, {
            x: 130,
            y: 285,
            r: 1
          }, {
            x: 370,
            y: 285,
            r: 1
          }, {
            x: 220,
            y: 310,
            r: 1.5
          }, {
            x: 280,
            y: 310,
            r: 1.5
          }, {
            x: 250,
            y: 350,
            r: 1.5
          }, {
            x: 155,
            y: 350,
            r: 1
          }, {
            x: 345,
            y: 350,
            r: 1
          }, {
            x: 195,
            y: 375,
            r: 1.2
          }, {
            x: 305,
            y: 375,
            r: 1.2
          }, {
            x: 250,
            y: 400,
            r: 1.5
          }].map((n, i) => <circle key={`n-${i}`} cx={n.x} cy={n.y} r={n.r} fill="white" opacity="0.35" data-id="element-317" data-mp="30qhjblorqmfe" data-mpi={i} />)}

            {/* ── Twinkling Stars — sharp pinpoint sparkles ── */}
            {[{
            x: 250,
            y: 140,
            d: 0
          }, {
            x: 200,
            y: 175,
            d: 1.2
          }, {
            x: 300,
            y: 175,
            d: 2.4
          }, {
            x: 250,
            y: 210,
            d: 0.6
          }, {
            x: 175,
            y: 245,
            d: 3.0
          }, {
            x: 325,
            y: 245,
            d: 1.8
          }, {
            x: 250,
            y: 280,
            d: 3.6
          }, {
            x: 220,
            y: 310,
            d: 0.9
          }, {
            x: 280,
            y: 310,
            d: 2.1
          }, {
            x: 250,
            y: 350,
            d: 4.0
          }, {
            x: 250,
            y: 400,
            d: 1.5
          }, {
            x: 155,
            y: 120,
            d: 2.8
          }, {
            x: 345,
            y: 120,
            d: 0.3
          }, {
            x: 130,
            y: 195,
            d: 3.3
          }, {
            x: 370,
            y: 195,
            d: 1.0
          }, {
            x: 130,
            y: 285,
            d: 4.2
          }, {
            x: 370,
            y: 285,
            d: 2.6
          }].map((s, i) => <motion.circle key={`tw-${i}`} cx={s.x} cy={s.y} r="1" fill="white" animate={{
            opacity: [0.15, 0.7, 0.15]
          }} transition={{
            duration: 3,
            delay: s.d,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-318" data-mp="26c3rryrli52e" data-mpi={i} />)}

            {/* ── Traveling Pulses — clean dots moving along lines ── */}
            {[{
            x1: 250,
            y1: 140,
            x2: 200,
            y2: 175,
            d: 0
          }, {
            x1: 250,
            y1: 140,
            x2: 300,
            y2: 175,
            d: 1.5
          }, {
            x1: 200,
            y1: 175,
            x2: 250,
            y2: 210,
            d: 3
          }, {
            x1: 300,
            y1: 175,
            x2: 250,
            y2: 210,
            d: 4
          }, {
            x1: 250,
            y1: 210,
            x2: 250,
            y2: 280,
            d: 2
          }, {
            x1: 175,
            y1: 245,
            x2: 250,
            y2: 280,
            d: 5
          }, {
            x1: 325,
            y1: 245,
            x2: 250,
            y2: 280,
            d: 3.5
          }, {
            x1: 250,
            y1: 280,
            x2: 250,
            y2: 350,
            d: 6
          }].map((p, i) => <motion.circle key={`p-${i}`} cx={p.x1} cy={p.y1} r="1.5" fill="white" animate={{
            cx: [p.x1, p.x2],
            cy: [p.y1, p.y2],
            opacity: [0, 0.5, 0.5, 0]
          }} transition={{
            duration: 1.5,
            delay: p.d,
            repeat: Infinity,
            repeatDelay: 4 + Math.random() * 3,
            ease: 'easeInOut'
          }} data-id="element-319" data-mp="36yzzsw0xikg2" data-mpi={i} />)}
          </svg>
        </motion.div>
      </div>

      <div className="inner-container relative z-10 pt-16 pb-16 lg:pt-24 lg:pb-24 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16" data-id="element-320" data-mp="2lal5wq5659wd">
        {/* Left Column - Copy */}
        <div className="flex-1 text-center lg:text-left" data-id="element-321" data-mp="2u2n2ueg1481w">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-322" data-mp="2u354xrpdhghq">
            <span className="eyebrow text-sm md:text-base" data-id="element-323" data-mp="26dkaabjuwusm">
              Just Talk & Say "Hey MR"...
            </span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-6" data-id="element-324" data-mp="2bakfylcsnvj7">
              Your Business. <br className="hidden lg:block" data-id="element-325" data-mp="2g8m25f5ioayw" />
              <span className="text-gradient" data-id="element-326" data-mp="3mpzf9vsh6zhh">Running Itself.</span>
            </h1>
            <p className="p-large mb-10 max-w-2xl mx-auto lg:mx-0" data-id="element-327" data-mp="3rpcbh758liwq">
              No prompts. No learning curves. No AI training required. Just talk
              to MISTR the way you would talk to a person — in any language —
              and watch your entire business run itself.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-328" data-mp="16c74d2gif3ph">
              <button className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-329" data-mp="16am9fbqa9ir3">
                Talk to MR
              </button>
              <button className="btn-ghost hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-shadow" data-id="element-330" data-mp="x9xw6j8prmf9">
                See How It Works
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}